import { sanitizeRsvpFontFamily } from "./layoutUtils";

const DARK_TEXT = "#1f2937";
const LIGHT_TEXT = "#f8fafc";

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function normalizeHex(color) {
  if (typeof color !== "string") return null;

  const trimmed = color.trim();

  if (/^#([0-9a-f]{3})$/i.test(trimmed)) {
    return `#${trimmed.slice(1).split("").map((char) => char + char).join("").toLowerCase()}`;
  }

  if (/^#([0-9a-f]{6})$/i.test(trimmed)) {
    return trimmed.toLowerCase();
  }

  return null;
}

function hexToRgb(color) {
  const normalized = normalizeHex(color);
  if (!normalized) return null;

  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

function channelToHex(value) {
  return clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0");
}

function mixColors(colorA, colorB, ratio = 0.5) {
  const rgbA = hexToRgb(colorA);
  const rgbB = hexToRgb(colorB);

  if (!rgbA || !rgbB) return normalizeHex(colorA) ?? normalizeHex(colorB);

  const weight = clamp(ratio, 0, 1);

  return `#${channelToHex(rgbA.r * weight + rgbB.r * (1 - weight))}${channelToHex(rgbA.g * weight + rgbB.g * (1 - weight))}${channelToHex(rgbA.b * weight + rgbB.b * (1 - weight))}`;
}

function toRgba(color, alpha) {
  const rgb = hexToRgb(color);
  if (!rgb) return null;

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${clamp(alpha, 0, 1).toFixed(2)})`;
}

function toLinearChannel(value) {
  const channel = value / 255;
  return channel <= 0.03928
    ? channel / 12.92
    : ((channel + 0.055) / 1.055) ** 2.4;
}

function getLuminance(color) {
  const rgb = hexToRgb(color);
  if (!rgb) return null;

  return (0.2126 * toLinearChannel(rgb.r))
    + (0.7152 * toLinearChannel(rgb.g))
    + (0.0722 * toLinearChannel(rgb.b));
}

function getContrastRatio(colorA, colorB) {
  const luminanceA = getLuminance(colorA);
  const luminanceB = getLuminance(colorB);

  if (luminanceA == null || luminanceB == null) return null;

  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);

  return (lighter + 0.05) / (darker + 0.05);
}

function isDark(color) {
  const luminance = getLuminance(color);
  return luminance != null && luminance < 0.45;
}

function getContrastColor(color, light = LIGHT_TEXT, dark = DARK_TEXT) {
  return isDark(color) ? light : dark;
}

function resolveTextColor(backgroundColor, preferredColor) {
  const backgroundHex = normalizeHex(backgroundColor);
  const preferredHex = normalizeHex(preferredColor);

  if (!backgroundHex) {
    return preferredHex;
  }

  const fallback = getContrastColor(backgroundHex);

  if (!preferredHex) {
    return fallback;
  }

  const contrast = getContrastRatio(backgroundHex, preferredHex);

  return contrast != null && contrast >= 4 ? preferredHex : fallback;
}

function buildButtonBackground(accentColor, surfaceColor) {
  const accentHex = normalizeHex(accentColor);
  const surfaceHex = normalizeHex(surfaceColor);

  if (!accentHex) return null;

  const start = mixColors(accentHex, "#ffffff", 0.14) ?? accentHex;
  const end = surfaceHex
    ? mixColors(accentHex, surfaceHex, 0.78) ?? accentHex
    : mixColors(accentHex, "#000000", 0.82) ?? accentHex;

  return `linear-gradient(135deg, ${start}, ${end})`;
}

function getButtonTextColor(backgroundColor, configuredTextColor, fallbackTextColor) {
  const backgroundHex = normalizeHex(backgroundColor);
  const configuredTextHex = normalizeHex(configuredTextColor);

  if (backgroundHex) {
    return resolveTextColor(backgroundHex, configuredTextHex ?? fallbackTextColor);
  }

  return configuredTextHex ?? fallbackTextColor;
}

function buildSectionTheme({
  sectionBgHex,
  sectionTextHex,
  sectionTitleHex,
  fontFamily,
  inputBgHex,
  inputTextHex,
  inputPlaceholderHex,
  fallbackSectionBg,
  fallbackTitleColor,
  fallbackTextColor,
  fallbackInputExtra,
  fallbackInputPlaceholderColor,
  fallbackFontFamily,
  baseBackgroundHex,
  accentHex,
}) {
  const sectionBackground = sectionBgHex ?? fallbackSectionBg;
  const sectionSurfaceHex = normalizeHex(sectionBackground) ?? baseBackgroundHex ?? "#ffffff";
  const sectionIsDark = isDark(sectionSurfaceHex);
  const sectionTextColor = sectionTextHex
    ?? (fallbackTextColor ?? resolveTextColor(sectionSurfaceHex, accentHex ?? DARK_TEXT));
  const sectionTitleColor = sectionTitleHex
    ?? (fallbackTitleColor ?? sectionTextColor);
  const defaultInputBackground = fallbackInputExtra?.background
    ?? (mixColors(sectionSurfaceHex, "#ffffff", sectionIsDark ? 0.12 : 0.94) ?? "#ffffff");
  const inputBackground = inputBgHex ?? defaultInputBackground;
  const inputSurfaceHex = normalizeHex(inputBackground) ?? sectionSurfaceHex;
  const fallbackInputTextHex = normalizeHex(fallbackInputExtra?.color);
  const inputColor = inputTextHex
    ? resolveTextColor(inputSurfaceHex, inputTextHex)
    : fallbackInputTextHex
      ? resolveTextColor(inputSurfaceHex, fallbackInputTextHex)
      : resolveTextColor(inputSurfaceHex, sectionTextColor ?? accentHex ?? DARK_TEXT);
  const inputBorderColor = (sectionBgHex || sectionTextHex || inputBgHex || inputTextHex || fontFamily)
    ? toRgba(sectionTextColor ?? accentHex ?? DARK_TEXT, sectionIsDark ? 0.36 : 0.24) ?? fallbackInputExtra?.borderColor
    : fallbackInputExtra?.borderColor;
  const inputPlaceholderColor = inputPlaceholderHex
    ?? (inputTextHex
      ? toRgba(inputColor, 0.65) ?? inputColor
      : fallbackInputPlaceholderColor
        ?? (sectionTextColor ? toRgba(sectionTextColor, 0.58) ?? sectionTextColor : "#9ca3af"));

  return {
    background: sectionBackground,
    titleColor: sectionTitleColor,
    textColor: sectionTextColor,
    fontFamily: fontFamily ?? fallbackFontFamily ?? null,
    input: {
      ...fallbackInputExtra,
      background: inputBackground,
      borderColor: inputBorderColor,
      color: inputColor,
      fontFamily: fontFamily ?? fallbackFontFamily ?? fallbackInputExtra?.fontFamily,
    },
    inputPlaceholderColor,
  };
}

export function getRsvpTheme(baseTheme, layoutConfig) {
  const backgroundHex = normalizeHex(layoutConfig?.rsvpBg);
  const configuredTextHex = normalizeHex(layoutConfig?.rsvpTextColor);
  const sectionBgHex = normalizeHex(layoutConfig?.rsvpSectionBg);
  const sectionTitleHex = normalizeHex(layoutConfig?.rsvpSectionTitleColor);
  const peopleBgHex = normalizeHex(layoutConfig?.rsvpPeopleBg);
  const peopleTextHex = normalizeHex(layoutConfig?.rsvpPeopleTextColor);
  const peopleTitleHex = normalizeHex(layoutConfig?.rsvpPeopleTitleColor);
  const peopleFontFamily = sanitizeRsvpFontFamily(layoutConfig?.rsvpPeopleFontFamily);
  const peopleInputBgHex = normalizeHex(layoutConfig?.rsvpPeopleInputBg);
  const peopleInputTextHex = normalizeHex(layoutConfig?.rsvpPeopleInputTextColor);
  const peopleInputPlaceholderHex = normalizeHex(layoutConfig?.rsvpPeopleInputPlaceholderColor);
  const messageBgHex = normalizeHex(layoutConfig?.rsvpMessageBg);
  const messageTextHex = normalizeHex(layoutConfig?.rsvpMessageTextColor);
  const messageTitleHex = normalizeHex(layoutConfig?.rsvpMessageTitleColor);
  const messageFontFamily = sanitizeRsvpFontFamily(layoutConfig?.rsvpMessageFontFamily);
  const messageInputBgHex = normalizeHex(layoutConfig?.rsvpMessageInputBg);
  const messageInputTextHex = normalizeHex(layoutConfig?.rsvpMessageInputTextColor);
  const messageInputPlaceholderHex = normalizeHex(layoutConfig?.rsvpMessageInputPlaceholderColor);
  const closedBgHex = normalizeHex(layoutConfig?.rsvpClosedBg);
  const closedTitleHex = normalizeHex(layoutConfig?.rsvpClosedTitleColor);
  const closedTitleWeight = ["normal", "bold"].includes(layoutConfig?.rsvpClosedTitleWeight)
    ? layoutConfig.rsvpClosedTitleWeight
    : null;
  const closedTextHex = normalizeHex(layoutConfig?.rsvpClosedTextColor);
  const addButtonBgHex = normalizeHex(layoutConfig?.rsvpAddButtonBg);
  const addButtonTextHex = normalizeHex(layoutConfig?.rsvpAddButtonTextColor);
  const submitButtonBgHex = normalizeHex(layoutConfig?.rsvpSubmitButtonBg);
  const submitButtonTextHex = normalizeHex(layoutConfig?.rsvpSubmitButtonTextColor);
  const inputBgHex = normalizeHex(layoutConfig?.rsvpInputBg);
  const inputTextHex = normalizeHex(layoutConfig?.rsvpInputTextColor);
  const inputPlaceholderHex = normalizeHex(layoutConfig?.rsvpInputPlaceholderColor);
  const hasGeneralSurfaceOverride = Boolean(backgroundHex || configuredTextHex);
  const hasAnyOverride = Boolean(
    hasGeneralSurfaceOverride
    || sectionBgHex
    || sectionTitleHex
    || peopleBgHex
    || peopleTextHex
    || peopleTitleHex
    || peopleFontFamily
    || peopleInputBgHex
    || peopleInputTextHex
    || peopleInputPlaceholderHex
    || messageBgHex
    || messageTextHex
    || messageTitleHex
    || messageFontFamily
    || messageInputBgHex
    || messageInputTextHex
    || messageInputPlaceholderHex
    || closedBgHex
    || closedTitleHex
    || closedTitleWeight
    || closedTextHex
    || addButtonBgHex
    || addButtonTextHex
    || submitButtonBgHex
    || submitButtonTextHex
    || inputBgHex
    || inputTextHex
    || inputPlaceholderHex
  );

  if (!hasAnyOverride) {
    return baseTheme;
  }

  const preferredText = configuredTextHex
    ?? normalizeHex(baseTheme.labelColor)
    ?? normalizeHex(baseTheme.titulo?.color)
    ?? DARK_TEXT;

  const textHex = hasGeneralSurfaceOverride
    ? (backgroundHex ? resolveTextColor(backgroundHex, preferredText) : preferredText)
    : preferredText;
  const themeBase = backgroundHex ?? "#ffffff";
  const darkSurface = isDark(themeBase);
  const accentHex = hasGeneralSurfaceOverride
    ? (textHex ?? getContrastColor(themeBase))
    : (normalizeHex(baseTheme.labelColor) ?? normalizeHex(baseTheme.titulo?.color) ?? DARK_TEXT);
  const buttonBackground = buildButtonBackground(accentHex, backgroundHex);
  const buttonBaseColor = mixColors(accentHex, backgroundHex ?? "#000000", 0.78) ?? accentHex;
  const buttonTextColor = resolveTextColor(buttonBaseColor, getContrastColor(buttonBaseColor));
  const sectionSurfaceHex = sectionBgHex ?? backgroundHex ?? "#ffffff";
  const sectionTitleColor = sectionTitleHex
    ?? resolveTextColor(
      sectionSurfaceHex,
      hasGeneralSurfaceOverride ? accentHex : normalizeHex(baseTheme.titulo?.color) ?? accentHex
    );
  const addButtonBackground = addButtonBgHex ?? (hasGeneralSurfaceOverride ? buttonBackground : baseTheme.btnAdd?.background);
  const addButtonColor = getButtonTextColor(
    addButtonBgHex,
    addButtonTextHex,
    hasGeneralSurfaceOverride ? buttonTextColor : baseTheme.btnAdd?.color
  );
  const submitButtonBackground = submitButtonBgHex ?? (hasGeneralSurfaceOverride ? buttonBackground : baseTheme.submit?.background);
  const submitButtonColor = getButtonTextColor(
    submitButtonBgHex,
    submitButtonTextHex,
    hasGeneralSurfaceOverride ? buttonTextColor : baseTheme.submit?.color
  );
  const defaultInputBackground = hasGeneralSurfaceOverride
    ? mixColors(backgroundHex, "#ffffff", darkSurface ? 0.08 : 0.94) ?? "white"
    : baseTheme.inputExtra?.background;
  const inputBackground = inputBgHex ?? defaultInputBackground;
  const inputTextColor = inputTextHex
    ?? (inputBgHex
      ? resolveTextColor(
        inputBgHex,
        hasGeneralSurfaceOverride
          ? accentHex
          : normalizeHex(baseTheme.inputExtra?.color) ?? normalizeHex(baseTheme.labelColor) ?? DARK_TEXT
      )
      : hasGeneralSurfaceOverride
        ? resolveTextColor(
          defaultInputBackground,
          accentHex
        )
        : baseTheme.inputExtra?.color);
  const inputPlaceholderColor = inputPlaceholderHex
    ?? (inputTextHex
      ? toRgba(inputTextHex, 0.65) ?? inputTextHex
      : inputBgHex
        ? toRgba(inputTextColor, 0.65) ?? inputTextColor
        : hasGeneralSurfaceOverride
          ? toRgba(accentHex, 0.58) ?? "#9ca3af"
          : baseTheme.inputPlaceholderColor);
  const resolvedSectionBg = sectionBgHex ?? (backgroundHex
    ? mixColors(backgroundHex, accentHex, darkSurface ? 0.12 : 0.08) ?? baseTheme.sectionBg
    : baseTheme.sectionBg);
  const resolvedRecadoBg = sectionBgHex ?? (backgroundHex
    ? mixColors(backgroundHex, accentHex, darkSurface ? 0.18 : 0.12) ?? baseTheme.recadoBg
    : baseTheme.recadoBg);
  const resolvedItemBg = hasGeneralSurfaceOverride
    ? mixColors(backgroundHex, "#ffffff", darkSurface ? 0.16 : 0.82) ?? baseTheme.itemBg
    : baseTheme.itemBg;
  const resolvedNumeroBg = hasGeneralSurfaceOverride
    ? mixColors(accentHex, backgroundHex, 0.88) ?? accentHex
    : baseTheme.numeroBg;
  const resolvedNumeroColor = hasGeneralSurfaceOverride
    ? getContrastColor(mixColors(accentHex, backgroundHex, 0.88) ?? accentHex)
    : baseTheme.numeroColor;
  const resolvedLabelColor = hasGeneralSurfaceOverride ? accentHex : baseTheme.labelColor;
  const resolvedInputExtra = {
    ...baseTheme.inputExtra,
    background: inputBackground,
    borderColor: hasGeneralSurfaceOverride
      ? toRgba(accentHex, darkSurface ? 0.42 : 0.24) ?? baseTheme.inputExtra?.borderColor
      : baseTheme.inputExtra?.borderColor,
    color: inputTextColor,
  };
  const peopleSection = buildSectionTheme({
    sectionBgHex: peopleBgHex ?? sectionBgHex,
    sectionTextHex: peopleTextHex,
    sectionTitleHex: peopleTitleHex,
    fontFamily: peopleFontFamily,
    inputBgHex: peopleInputBgHex ?? inputBgHex,
    inputTextHex: peopleInputTextHex ?? inputTextHex,
    inputPlaceholderHex: peopleInputPlaceholderHex ?? inputPlaceholderHex,
    fallbackSectionBg: resolvedSectionBg,
    fallbackTitleColor: sectionTitleColor,
    fallbackTextColor: resolvedLabelColor,
    fallbackInputExtra: resolvedInputExtra,
    fallbackInputPlaceholderColor: inputPlaceholderColor,
    fallbackFontFamily: null,
    baseBackgroundHex: backgroundHex ?? normalizeHex(resolvedSectionBg) ?? "#ffffff",
    accentHex,
  });
  const messageSection = buildSectionTheme({
    sectionBgHex: messageBgHex ?? sectionBgHex,
    sectionTextHex: messageTextHex,
    sectionTitleHex: messageTitleHex,
    fontFamily: messageFontFamily,
    inputBgHex: messageInputBgHex ?? inputBgHex,
    inputTextHex: messageInputTextHex ?? inputTextHex,
    inputPlaceholderHex: messageInputPlaceholderHex ?? inputPlaceholderHex,
    fallbackSectionBg: resolvedRecadoBg,
    fallbackTitleColor: sectionTitleColor,
    fallbackTextColor: sectionTitleColor ?? resolvedLabelColor,
    fallbackInputExtra: resolvedInputExtra,
    fallbackInputPlaceholderColor: inputPlaceholderColor,
    fallbackFontFamily: null,
    baseBackgroundHex: backgroundHex ?? normalizeHex(resolvedRecadoBg) ?? "#ffffff",
    accentHex,
  });
  const peopleSurfaceHex = normalizeHex(peopleSection.background) ?? backgroundHex ?? "#ffffff";
  const peopleSurfaceDark = isDark(peopleSurfaceHex);
  const peopleAccentHex = normalizeHex(peopleSection.textColor) ?? accentHex ?? DARK_TEXT;
  const peopleHasResolvedOverride = Boolean(
    hasGeneralSurfaceOverride
    || sectionBgHex
    || sectionTitleHex
    || peopleBgHex
    || peopleTextHex
    || peopleTitleHex
    || peopleFontFamily
    || peopleInputBgHex
    || peopleInputTextHex
    || peopleInputPlaceholderHex
  );
  const peopleItemBg = peopleHasResolvedOverride
    ? mixColors(peopleSurfaceHex, "#ffffff", peopleSurfaceDark ? 0.16 : 0.82) ?? resolvedItemBg
    : resolvedItemBg;
  const peopleNumberBg = peopleHasResolvedOverride
    ? mixColors(peopleAccentHex, peopleSurfaceHex, 0.88) ?? resolvedNumeroBg
    : resolvedNumeroBg;
  const peopleNumberColor = peopleHasResolvedOverride
    ? getContrastColor(peopleNumberBg)
    : resolvedNumeroColor;
  const addButtonBoxShadow = addButtonBgHex
    ? `0 3px 10px ${toRgba(addButtonBgHex, darkSurface ? 0.30 : 0.24) ?? "rgba(0,0,0,0.18)"}`
    : hasGeneralSurfaceOverride
      ? `0 3px 10px ${toRgba(accentHex, darkSurface ? 0.30 : 0.24) ?? "rgba(0,0,0,0.18)"}`
      : baseTheme.btnAdd?.boxShadow;
  const resolvedClosedBg = closedBgHex
    ?? (hasGeneralSurfaceOverride
      ? mixColors(backgroundHex, accentHex, darkSurface ? 0.14 : 0.08)
      : baseTheme.closedBg);
  const closedSurfaceHex = normalizeHex(resolvedClosedBg) ?? backgroundHex ?? "#ffffff";
  const closedSurfaceDark = isDark(closedSurfaceHex);
  const closedAccentHex = closedTitleHex ?? closedTextHex ?? accentHex ?? DARK_TEXT;
  const resolvedClosedTitleColor = closedTitleHex ?? baseTheme.closedTitleColor ?? baseTheme.titulo?.color;
  const resolvedClosedTextColor = closedTextHex
    ?? (hasGeneralSurfaceOverride
      ? (toRgba(accentHex, 0.72) ?? "#666666")
      : baseTheme.mutedTextColor);
  const resolvedClosedBorder = (closedBgHex || closedTitleHex || closedTextHex)
    ? toRgba(closedAccentHex, closedSurfaceDark ? 0.34 : 0.18) ?? baseTheme.closedBorder
    : hasGeneralSurfaceOverride
      ? toRgba(accentHex, darkSurface ? 0.34 : 0.18)
      : baseTheme.closedBorder;
  const submitButtonBoxShadow = submitButtonBgHex
    ? `0 5px 15px ${toRgba(submitButtonBgHex, darkSurface ? 0.36 : 0.28) ?? "rgba(0,0,0,0.18)"}`
    : hasGeneralSurfaceOverride
      ? `0 5px 15px ${toRgba(accentHex, darkSurface ? 0.36 : 0.28) ?? "rgba(0,0,0,0.18)"}`
      : baseTheme.submit?.boxShadow;

  return {
    ...baseTheme,
    sectionBg: peopleSection.background,
    recadoBg: messageSection.background,
    itemBg: peopleItemBg,
    numeroBg: peopleNumberBg,
    numeroColor: peopleNumberColor,
    labelColor: peopleSection.textColor,
    titulo: {
      ...baseTheme.titulo,
      color: peopleSection.titleColor,
      fontFamily: peopleSection.fontFamily ?? baseTheme.titulo?.fontFamily,
    },
    sectionTitleColor: messageSection.titleColor,
    inputExtra: peopleSection.input,
    inputPlaceholderColor: peopleSection.inputPlaceholderColor,
    btnAdd: {
      ...baseTheme.btnAdd,
      background: addButtonBackground,
      color: addButtonColor,
      boxShadow: addButtonBoxShadow,
      fontFamily: peopleSection.fontFamily ?? baseTheme.btnAdd?.fontFamily,
    },
    submit: {
      ...baseTheme.submit,
      background: submitButtonBackground,
      color: submitButtonColor,
      boxShadow: submitButtonBoxShadow,
    },
    formTextColor: hasGeneralSurfaceOverride ? accentHex : baseTheme.formTextColor,
    mutedTextColor: hasGeneralSurfaceOverride ? (toRgba(accentHex, 0.72) ?? "#666666") : baseTheme.mutedTextColor,
    closedBg: resolvedClosedBg,
    closedBorder: resolvedClosedBorder,
    closedTitleColor: resolvedClosedTitleColor,
    closedTitleWeight: closedTitleWeight ?? baseTheme.closedTitleWeight,
    closedTextColor: resolvedClosedTextColor,
    people: {
      sectionBg: peopleSection.background,
      titleColor: peopleSection.titleColor,
      textColor: peopleSection.textColor,
      fontFamily: peopleSection.fontFamily,
      input: peopleSection.input,
      inputPlaceholderColor: peopleSection.inputPlaceholderColor,
      itemBg: peopleItemBg,
      numberBg: peopleNumberBg,
      numberColor: peopleNumberColor,
      addButton: {
        ...baseTheme.btnAdd,
        background: addButtonBackground,
        color: addButtonColor,
        boxShadow: addButtonBoxShadow,
        fontFamily: peopleSection.fontFamily ?? baseTheme.btnAdd?.fontFamily,
      },
    },
    message: {
      sectionBg: messageSection.background,
      titleColor: messageSection.titleColor,
      textColor: messageSection.textColor,
      fontFamily: messageSection.fontFamily,
      input: messageSection.input,
      inputPlaceholderColor: messageSection.inputPlaceholderColor,
    },
  };
}

export function getRsvpCssVars(theme) {
  return {
    "--rsvp-input-placeholder-color": theme.inputPlaceholderColor ?? theme.people?.inputPlaceholderColor ?? "#9ca3af",
    "--rsvp-people-placeholder-color": theme.people?.inputPlaceholderColor ?? theme.inputPlaceholderColor ?? "#9ca3af",
    "--rsvp-message-placeholder-color": theme.message?.inputPlaceholderColor ?? theme.inputPlaceholderColor ?? "#9ca3af",
  };
}
